import React, { useState, useEffect } from 'react';
import { X, AlertTriangle, Clock, Calendar, Package } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ExpiryAlertModal({ token, isOpen, onClose, bodega }) {
  const [loading, setLoading] = useState(true);
  const [expiryData, setExpiryData] = useState({ expired: [], nearExpiry: [] });

  useEffect(() => {
    if (isOpen && token) {
      fetchExpiryData();
    }
  }, [isOpen, token, bodega]);

  const fetchExpiryData = async () => {
    setLoading(true);
    try {
      // Pasar bodega como filtro para obtener datos específicos
      const bodegaParam = bodega && bodega !== 'N/A' && bodega !== 'ALL' && bodega !== 'todas' ? `?bodega_filter=${bodega}` : '';
      const response = await fetch(`${API_URL}/api/alerts/expiry-check${bodegaParam}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        const expired = (data.products_already_expired || []);
        const nearExpiry = (data.products_near_expiry || []);
        setExpiryData({ expired, nearExpiry });
      }
    } catch (error) {
      console.error('Error fetching expiry data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const totalAlerts = expiryData.expired.length + expiryData.nearExpiry.length;

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[80vh] overflow-hidden animate-in fade-in zoom-in duration-300">
        {/* Header */}
        <div className="bg-gradient-to-r from-orange-500 to-red-500 text-white p-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="bg-white/20 p-2 rounded-lg">
                <AlertTriangle className="w-6 h-6" />
              </div>
              <div>
                <h2 className="text-xl font-bold">⚠️ Alerta de Vencimientos</h2>
                <p className="text-orange-100 text-sm">Bodega: {bodega}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/20 rounded-full transition-colors"
              data-testid="expiry-modal-close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-5 overflow-y-auto max-h-[60vh]">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-orange-500"></div>
            </div>
          ) : totalAlerts === 0 ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Package className="w-8 h-8 text-green-500" />
              </div>
              <h3 className="text-xl font-semibold text-gray-800 mb-2">¡Todo en orden!</h3>
              <p className="text-gray-600">No hay productos vencidos ni próximos a vencer.</p>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Productos ya vencidos */}
              {expiryData.expired.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <div className="bg-red-100 p-1.5 rounded-lg">
                      <AlertTriangle className="w-4 h-4 text-red-600" />
                    </div>
                    <h3 className="font-semibold text-red-700">
                      Productos Vencidos ({expiryData.expired.length})
                    </h3>
                  </div>
                  <div className="bg-red-50 rounded-xl overflow-hidden border border-red-200">
                    <table className="w-full text-sm">
                      <thead className="bg-red-100">
                        <tr>
                          <th className="text-left p-3 font-medium text-red-800">SKU</th>
                          <th className="text-left p-3 font-medium text-red-800">Producto</th>
                          <th className="text-left p-3 font-medium text-red-800">Lote</th>
                          <th className="text-center p-3 font-medium text-red-800">Bodega</th>
                          <th className="text-center p-3 font-medium text-red-800">Stock</th>
                          <th className="text-center p-3 font-medium text-red-800">Vencido</th>
                        </tr>
                      </thead>
                      <tbody>
                        {expiryData.expired.slice(0, 10).map((product, idx) => (
                          <tr key={idx} className="border-t border-red-200">
                            <td className="p-3 font-mono text-xs">{product.sku}</td>
                            <td className="p-3 text-gray-700">{product.description?.substring(0, 30)}...</td>
                            <td className="p-3 font-mono text-xs">{product.lote || 'N/A'}</td>
                            <td className="p-3 text-center text-xs font-medium">{product.bodega || '-'}</td>
                            <td className="p-3 text-center font-semibold">{product.current_stock}</td>
                            <td className="p-3 text-center">
                              <span className="bg-red-600 text-white px-2 py-1 rounded-full text-xs font-medium">
                                {Math.abs(product.days_to_expiry || 0)} días
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {expiryData.expired.length > 10 && (
                      <div className="p-3 text-center text-red-600 bg-red-100 text-sm">
                        +{expiryData.expired.length - 10} productos más...
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Productos próximos a vencer */}
              {expiryData.nearExpiry.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <div className="bg-orange-100 p-1.5 rounded-lg">
                      <Clock className="w-4 h-4 text-orange-600" />
                    </div>
                    <h3 className="font-semibold text-orange-700">
                      Próximos a Vencer ({expiryData.nearExpiry.length})
                    </h3>
                  </div>
                  <div className="bg-orange-50 rounded-xl overflow-hidden border border-orange-200">
                    <table className="w-full text-sm">
                      <thead className="bg-orange-100">
                        <tr>
                          <th className="text-left p-3 font-medium text-orange-800">SKU</th>
                          <th className="text-left p-3 font-medium text-orange-800">Producto</th>
                          <th className="text-left p-3 font-medium text-orange-800">Lote</th>
                          <th className="text-center p-3 font-medium text-orange-800">Bodega</th>
                          <th className="text-center p-3 font-medium text-orange-800">Stock</th>
                          <th className="text-center p-3 font-medium text-orange-800">Vence en</th>
                        </tr>
                      </thead>
                      <tbody>
                        {expiryData.nearExpiry.slice(0, 10).map((product, idx) => (
                          <tr key={idx} className="border-t border-orange-200">
                            <td className="p-3 font-mono text-xs">{product.sku}</td>
                            <td className="p-3 text-gray-700">{product.description?.substring(0, 30)}...</td>
                            <td className="p-3 font-mono text-xs">{product.lote || 'N/A'}</td>
                            <td className="p-3 text-center text-xs font-medium">{product.bodega || '-'}</td>
                            <td className="p-3 text-center font-semibold">{product.current_stock}</td>
                            <td className="p-3 text-center">
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                product.days_to_expiry <= 30 
                                  ? 'bg-orange-500 text-white' 
                                  : 'bg-yellow-400 text-yellow-900'
                              }`}>
                                {product.days_to_expiry} días
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {expiryData.nearExpiry.length > 10 && (
                      <div className="p-3 text-center text-orange-600 bg-orange-100 text-sm">
                        +{expiryData.nearExpiry.length - 10} productos más...
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-5 py-4 border-t flex justify-between items-center">
          <p className="text-sm text-gray-500">
            <Calendar className="w-4 h-4 inline mr-1" />
            Actualizado: {new Date().toLocaleString('es-CR')}
          </p>
          <button
            onClick={onClose}
            className="bg-gray-800 text-white px-6 py-2 rounded-lg hover:bg-gray-700 transition-colors font-medium"
            data-testid="expiry-modal-understood"
          >
            Entendido
          </button>
        </div>
      </div>
    </div>
  );
}
